import { Radio, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import { Notify, toast } from "@/components/ui/notify";
import { isApiClientError } from "@/lib/api-client";
import type { DeviceSummary } from "@/features/devices/hooks";
import type { CardSummary } from "@/features/cards/hooks";
import { useDeleteSensor, type SensorSummary } from "@/features/sensors/hooks";
import { Breadcrumb } from "./Breadcrumb";
import { UuidLine } from "./UuidLine";
import { SensorEditForm } from "./SensorEditForm";
import { SensorLinkSection } from "./SensorLinkSection";
import {
  SENSOR_STATE_LABEL,
  SENSOR_STATE_TONE,
  SENSOR_STATUS_LABEL,
  SENSOR_STATUS_TONE,
} from "./sensor-display";

interface SensorDetailPaneProps {
  device: DeviceSummary;
  card: CardSummary;
  sensor: SensorSummary;
  onSelectClp: () => void;
  onSelectCard: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onDeleted: () => void;
}

export function SensorDetailPane({
  device,
  card,
  sensor,
  onSelectClp,
  onSelectCard,
  onDirtyChange,
  onDeleted,
}: SensorDetailPaneProps) {
  const del = useDeleteSensor();

  async function onDelete() {
    const ok = await Notify.confirm({
      variant: "danger",
      title: `Excluir sensor "${sensor.code}"?`,
      confirmLabel: "Excluir",
      typeToConfirm: sensor.code,
    });
    if (!ok) return;
    try {
      await del.mutateAsync(sensor.id);
      toast.success("Sensor excluído.");
      onDeleted();
    } catch (err) {
      toast.error("Falha ao excluir.", {
        description: isApiClientError(err) ? err.error.message : "Erro inesperado.",
      });
    }
  }

  return (
    <div className="space-y-5">
      <Breadcrumb
        parts={[
          { label: device.code, mono: true, onClick: onSelectClp },
          { label: card.code, mono: true, onClick: onSelectCard },
          { label: sensor.position != null ? `pos ${sensor.position}` : sensor.code },
        ]}
      />
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-[12px] text-text-secondary">
            <Radio className="h-3.5 w-3.5" /> Sensor
          </div>
          <h2 className="font-mono text-[20px] font-semibold text-text-primary">{sensor.code}</h2>
          <UuidLine uuid={sensor.id} />
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Pill tone={SENSOR_STATE_TONE[sensor.state]} withDot>
            {SENSOR_STATE_LABEL[sensor.state]}
          </Pill>
          <Pill tone={SENSOR_STATUS_TONE[sensor.status]} withDot>
            {SENSOR_STATUS_LABEL[sensor.status]}
          </Pill>
          <Button variant="ghost" size="sm" onClick={() => void onDelete()}>
            <Trash2 className="h-4 w-4" /> Excluir
          </Button>
        </div>
      </div>

      <section className="space-y-3">
        <div className="text-[13px] font-medium text-text-secondary">Definição</div>
        <SensorEditForm deviceId={device.id} sensor={sensor} onDirtyChange={onDirtyChange} />
        <div className="grid grid-cols-2 gap-3 text-[12px] sm:grid-cols-3">
          <div>
            <span className="uppercase tracking-wide text-text-tertiary">Posição</span>
            <div className="font-mono text-text-primary">
              {sensor.position != null ? sensor.position : "—"}
            </div>
          </div>
          <div>
            <span className="uppercase tracking-wide text-text-tertiary">Unidade</span>
            <div className="text-text-primary">{sensor.unit ?? "—"}</div>
          </div>
        </div>
      </section>

      <SensorLinkSection sensor={sensor} />
    </div>
  );
}
